/**
 * Runtime capability detection.
 *
 * The single rule this module obeys: **report what is, never what is hoped for.** Where a
 * feature can be proved by using it cheaply — opening the origin-private file system,
 * opening an IndexedDB database, compiling an AudioWorklet processor — it is proved rather
 * than sniffed, because `'audioWorklet' in ctx` has been true on browsers where
 * `addModule()` rejects. Where proving it would cost the user something (a microphone
 * prompt, a persistent-storage prompt, an audio hardware unit) the presence of the API is
 * reported *as* presence of the API, and the detail string says so in those words.
 *
 * Nothing here asks for a permission. Nothing here starts a microphone. The report is safe
 * to build on launch.
 */

import { h } from '../core/dom';

// MARK: - Types

export type SupportLevel = 'full' | 'partial' | 'none';

export type CapabilityGroup = 'audio' | 'recording' | 'storage' | 'system' | 'display';

export type CapabilityId =
  | 'audio-session'
  | 'audio-worklet'
  | 'offline-audio-context'
  | 'web-codecs-audio'
  | 'shared-memory'
  | 'wasm'
  | 'media-recorder'
  | 'get-user-media'
  | 'opfs'
  | 'opfs-sync-access'
  | 'indexeddb'
  | 'persistent-storage'
  | 'service-worker'
  | 'background-sync'
  | 'push'
  | 'badging'
  | 'web-share'
  | 'web-share-files'
  | 'file-system-access'
  | 'wake-lock'
  | 'media-session'
  | 'vibration'
  | 'view-transitions'
  | 'standalone-display'
  | 'install-prompt';

export interface CapabilityRow {
  readonly id: CapabilityId;
  readonly label: string;
  readonly group: CapabilityGroup;
  readonly support: SupportLevel;
  /** What was observed, in one sentence. Never a promise about another browser. */
  readonly detail: string;
  /** What PadLab does instead when support is not `full`. `null` when nothing is needed. */
  readonly fallback: string | null;
}

export interface AudioEnvironment {
  /** Sample rate the hardware actually gave us, not the rate we asked for. */
  readonly sampleRate: number | null;
  readonly baseLatencySeconds: number | null;
  readonly outputLatencySeconds: number | null;
  readonly maxChannelCount: number | null;
  readonly state: string | null;
  /**
   * `live` means the numbers came from the engine's own context and are what the player is
   * hearing. `probe` means a throwaway context was opened and closed to read them, which is
   * accurate for sample rate but reports latency for a context that never ran.
   */
  readonly source: 'live' | 'probe' | 'unavailable';
}

export interface StorageEnvironment {
  readonly persisted: boolean;
  readonly canRequestPersistence: boolean;
  readonly usageBytes: number | null;
  readonly quotaBytes: number | null;
}

export type DisplayMode =
  | 'browser'
  | 'minimal-ui'
  | 'standalone'
  | 'fullscreen'
  | 'window-controls-overlay'
  | 'picture-in-picture';

export interface DisplayEnvironment {
  readonly displayMode: DisplayMode;
  /** iOS Safari's own flag, which predates and disagrees with `display-mode` on old builds. */
  readonly legacyStandalone: boolean;
  readonly devicePixelRatio: number;
  readonly viewportWidth: number;
  readonly viewportHeight: number;
  readonly reducedMotion: boolean;
  readonly reducedTransparency: boolean;
  readonly increasedContrast: boolean;
}

export interface CapabilityReport {
  readonly generatedAt: string;
  readonly secureContext: boolean;
  readonly crossOriginIsolated: boolean;
  readonly capabilities: readonly CapabilityRow[];
  /** Every MediaRecorder container/codec pair this engine accepted, best first. */
  readonly recorderMimeTypes: readonly string[];
  readonly audio: AudioEnvironment;
  readonly storage: StorageEnvironment;
  readonly display: DisplayEnvironment;
}

export interface ProbeOptions {
  /**
   * The engine's live AudioContext. Passing it makes the latency figures real rather than
   * theoretical; without it a suspended probe context is opened and immediately closed.
   */
  readonly audioContext?: BaseAudioContext;
}

// MARK: - MediaRecorder negotiation
//
// Ordered by what PadLab would rather have. AAC in an MP4 container is the only thing every
// current iPhone can both record and hand to another app; Opus is smaller and is what
// Chromium gives us; WAV appears on nothing today but costs one string to ask for.

const RECORDER_CANDIDATES = [
  'audio/mp4;codecs=mp4a.40.2',
  'audio/mp4',
  'audio/aac',
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/ogg;codecs=opus',
  'audio/ogg',
  'audio/wav',
] as const;

function supportedRecorderTypes(): string[] {
  if (typeof MediaRecorder !== 'function' || typeof MediaRecorder.isTypeSupported !== 'function') {
    return [];
  }
  return RECORDER_CANDIDATES.filter((type) => {
    try {
      return MediaRecorder.isTypeSupported(type);
    } catch {
      return false;
    }
  });
}

// MARK: - Display

export function readDisplayMode(): DisplayMode {
  const modes: DisplayMode[] = [
    'window-controls-overlay',
    'fullscreen',
    'standalone',
    'minimal-ui',
    'browser',
  ];
  for (const mode of modes) {
    if (window.matchMedia(`(display-mode: ${mode})`).matches) return mode;
  }
  return 'browser';
}

/** iOS Safari sets this on Home Screen launches; it is absent everywhere else. */
export function readLegacyStandalone(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

function readDisplayEnvironment(): DisplayEnvironment {
  return {
    displayMode: readDisplayMode(),
    legacyStandalone: readLegacyStandalone(),
    devicePixelRatio: window.devicePixelRatio,
    viewportWidth: Math.round(window.visualViewport?.width ?? window.innerWidth),
    viewportHeight: Math.round(window.visualViewport?.height ?? window.innerHeight),
    reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    reducedTransparency: window.matchMedia('(prefers-reduced-transparency: reduce)').matches,
    increasedContrast: window.matchMedia('(prefers-contrast: more)').matches,
  };
}

// MARK: - Functional probes

/**
 * Compiles a one-line processor from a blob URL. This is the only detection that separates
 * "the property exists" from "the render thread will actually run our DSP".
 */
async function probeAudioWorklet(): Promise<{ ok: boolean; reason: string }> {
  if (typeof AudioWorkletNode !== 'function') {
    return { ok: false, reason: 'AudioWorkletNode is not defined.' };
  }
  if (typeof OfflineAudioContext !== 'function') {
    return { ok: false, reason: 'OfflineAudioContext is not defined, so this could not be tested.' };
  }

  // An offline context is used deliberately: it needs no hardware, no user gesture, and no
  // audio session, so the probe cannot steal the route from a live engine.
  const context = new OfflineAudioContext({ numberOfChannels: 1, length: 128, sampleRate: 44100 });
  const worklet = (context as BaseAudioContext & { audioWorklet?: AudioWorklet }).audioWorklet;
  if (worklet === undefined) {
    return { ok: false, reason: 'BaseAudioContext.audioWorklet is missing.' };
  }

  const source = `registerProcessor('padlab-probe', class extends AudioWorkletProcessor { process() { return false; } });`;
  const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
  try {
    await worklet.addModule(url);
    return { ok: true, reason: 'A processor module compiled and registered on the render thread.' };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'addModule() was rejected.';
    return { ok: false, reason: `addModule() failed: ${message}` };
  } finally {
    URL.revokeObjectURL(url);
  }
}

interface OpfsProbe {
  readonly available: boolean;
  readonly writable: boolean;
  readonly syncAccess: boolean;
  readonly reason: string;
}

async function probeOpfs(): Promise<OpfsProbe> {
  const storage = navigator.storage as StorageManager & {
    getDirectory?: () => Promise<FileSystemDirectoryHandle>;
  };
  if (typeof storage?.getDirectory !== 'function') {
    return {
      available: false,
      writable: false,
      syncAccess: false,
      reason: 'navigator.storage.getDirectory is not implemented.',
    };
  }

  let root: FileSystemDirectoryHandle;
  try {
    root = await storage.getDirectory();
  } catch (error) {
    const message = error instanceof Error ? error.message : 'the request was rejected';
    return {
      available: false,
      writable: false,
      syncAccess: false,
      reason: `The origin-private file system could not be opened: ${message}.`,
    };
  }

  const probeName = '.padlab-capability-probe';
  try {
    const file = await root.getFileHandle(probeName, { create: true });
    const writable = typeof (file as FileSystemFileHandle).createWritable === 'function';
    const syncAccess =
      typeof (file as FileSystemFileHandle & { createSyncAccessHandle?: unknown })
        .createSyncAccessHandle === 'function';
    return {
      available: true,
      writable,
      syncAccess,
      reason: writable
        ? 'A file was created and closed in the origin-private file system.'
        : 'The directory opened but file handles have no createWritable().',
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'the write was rejected';
    return {
      available: true,
      writable: false,
      syncAccess: false,
      reason: `The directory opened but a file could not be created: ${message}.`,
    };
  } finally {
    // Never leave a probe file behind in the user's storage.
    await root.removeEntry(probeName).catch(() => undefined);
  }
}

async function probeIndexedDB(): Promise<{ ok: boolean; reason: string }> {
  if (typeof indexedDB === 'undefined') {
    return { ok: false, reason: 'indexedDB is not defined.' };
  }
  const name = '__padlab_capability_probe__';
  return await new Promise<{ ok: boolean; reason: string }>((resolve) => {
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(name, 1);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'open() threw';
      resolve({ ok: false, reason: `indexedDB.open() threw: ${message}.` });
      return;
    }
    request.onerror = () => {
      resolve({
        ok: false,
        reason: 'indexedDB.open() failed. Private browsing or a storage policy is blocking it.',
      });
    };
    request.onsuccess = () => {
      request.result.close();
      indexedDB.deleteDatabase(name);
      resolve({ ok: true, reason: 'A database opened, closed and was deleted successfully.' });
    };
    request.onblocked = () => {
      resolve({ ok: true, reason: 'A database opened but another tab is holding an older version.' });
    };
  });
}

function probeShareFiles(): boolean {
  const canShare = (navigator as Navigator & { canShare?: (data?: ShareData) => boolean }).canShare;
  if (typeof canShare !== 'function' || typeof navigator.share !== 'function') return false;
  try {
    // canShare() inspects the actual file list, so this has to be a real File of a type
    // PadLab would genuinely export.
    const probe = new File([new Uint8Array(4)], 'padlab-probe.wav', { type: 'audio/wav' });
    return canShare.call(navigator, { files: [probe] });
  } catch {
    return false;
  }
}

async function readAudioEnvironment(supplied?: BaseAudioContext): Promise<AudioEnvironment> {
  const empty: AudioEnvironment = {
    sampleRate: null,
    baseLatencySeconds: null,
    outputLatencySeconds: null,
    maxChannelCount: null,
    state: null,
    source: 'unavailable',
  };

  const read = (context: BaseAudioContext, source: 'live' | 'probe'): AudioEnvironment => {
    const realtime = context as AudioContext;
    const base = typeof realtime.baseLatency === 'number' ? realtime.baseLatency : null;
    const output = typeof realtime.outputLatency === 'number' ? realtime.outputLatency : null;
    return {
      sampleRate: context.sampleRate,
      baseLatencySeconds: base,
      outputLatencySeconds: output,
      maxChannelCount: context.destination.maxChannelCount,
      state: context.state,
      source,
    };
  };

  if (supplied !== undefined) return read(supplied, 'live');
  if (typeof AudioContext !== 'function') return empty;

  let probe: AudioContext | null = null;
  try {
    probe = new AudioContext({ latencyHint: 'interactive' });
    return read(probe, 'probe');
  } catch {
    return empty;
  } finally {
    // A probe context holds a hardware unit open on iOS. Always give it back.
    await probe?.close().catch(() => undefined);
  }
}

async function readStorageEnvironment(): Promise<StorageEnvironment> {
  const storage = navigator.storage as StorageManager | undefined;
  let persisted = false;
  let usage: number | null = null;
  let quota: number | null = null;

  if (typeof storage?.persisted === 'function') {
    persisted = await storage.persisted().catch(() => false);
  }
  if (typeof storage?.estimate === 'function') {
    const estimate = await storage.estimate().catch(() => null);
    usage = estimate?.usage ?? null;
    quota = estimate?.quota ?? null;
  }

  return {
    persisted,
    canRequestPersistence: typeof storage?.persist === 'function',
    usageBytes: usage,
    quotaBytes: quota,
  };
}

// MARK: - The report

function row(
  id: CapabilityId,
  group: CapabilityGroup,
  label: string,
  support: SupportLevel,
  detail: string,
  fallback: string | null,
): CapabilityRow {
  return { id, group, label, support, detail, fallback };
}

export async function probeCapabilities(options: ProbeOptions = {}): Promise<CapabilityReport> {
  // Independent probes, so they run together rather than in a chain the user waits through.
  const [worklet, opfs, idb, audio, storage] = await Promise.all([
    probeAudioWorklet(),
    probeOpfs(),
    probeIndexedDB(),
    readAudioEnvironment(options.audioContext),
    readStorageEnvironment(),
  ]);

  const recorderMimeTypes = supportedRecorderTypes();
  const display = readDisplayEnvironment();
  const rows: CapabilityRow[] = [];

  // --- Audio ---------------------------------------------------------------------------

  rows.push(
    row(
      'audio-worklet',
      'audio',
      'AudioWorklet',
      worklet.ok ? 'full' : 'none',
      worklet.reason,
      worklet.ok
        ? null
        : 'PadLab falls back to scheduled AudioBufferSourceNodes. Pads still trigger, but the mixer, limiter and live effect processing lose their dedicated render thread and become vulnerable to main-thread jank.',
    ),
  );

  const hasOffline = typeof OfflineAudioContext === 'function';
  rows.push(
    row(
      'offline-audio-context',
      'audio',
      'OfflineAudioContext',
      hasOffline ? 'full' : 'none',
      hasOffline
        ? 'Faster-than-real-time rendering is available for bounces and exports.'
        : 'OfflineAudioContext is not defined.',
      hasOffline
        ? null
        : 'Exports are rendered in real time through a MediaStreamDestination, which takes as long as the material lasts.',
    ),
  );

  const hasAudioCodecs =
    typeof (globalThis as { AudioDecoder?: unknown }).AudioDecoder === 'function' &&
    typeof (globalThis as { AudioEncoder?: unknown }).AudioEncoder === 'function';
  rows.push(
    row(
      'web-codecs-audio',
      'audio',
      'WebCodecs audio',
      hasAudioCodecs ? 'full' : 'none',
      hasAudioCodecs
        ? 'AudioDecoder and AudioEncoder are available for streaming decode and AAC export.'
        : 'AudioDecoder/AudioEncoder are not defined.',
      hasAudioCodecs
        ? null
        : 'Imports are decoded with decodeAudioData, which loads the whole file into memory, and exports are written as PCM WAV.',
    ),
  );

  const isolated = globalThis.crossOriginIsolated === true;
  const hasSAB = typeof SharedArrayBuffer === 'function';
  rows.push(
    row(
      'shared-memory',
      'audio',
      'Shared memory (SharedArrayBuffer)',
      isolated && hasSAB ? 'full' : 'none',
      isolated && hasSAB
        ? 'The page is cross-origin isolated, so PCM moves between the worklet and the analysis worker without copying.'
        : hasSAB
          ? 'SharedArrayBuffer exists but the page is not cross-origin isolated, so it cannot be used.'
          : 'SharedArrayBuffer is not defined.',
      isolated && hasSAB
        ? null
        : 'Audio blocks are transferred to the worker with postMessage instead. Correct, but it copies, which raises CPU during waveform analysis.',
    ),
  );

  const hasWasm = typeof WebAssembly === 'object' && typeof WebAssembly.instantiate === 'function';
  rows.push(
    row(
      'wasm',
      'audio',
      'WebAssembly',
      hasWasm ? 'full' : 'none',
      hasWasm ? 'WebAssembly is available for DSP kernels.' : 'WebAssembly is not defined.',
      hasWasm ? null : 'All DSP runs as plain JavaScript in the worklet.',
    ),
  );

  // --- Recording -----------------------------------------------------------------------

  rows.push(
    row(
      'media-recorder',
      'recording',
      'MediaRecorder',
      recorderMimeTypes.length > 0 ? 'full' : typeof MediaRecorder === 'function' ? 'partial' : 'none',
      recorderMimeTypes.length > 0
        ? `Accepted: ${recorderMimeTypes.join(', ')}.`
        : typeof MediaRecorder === 'function'
          ? 'MediaRecorder exists but rejected every audio container PadLab asked about.'
          : 'MediaRecorder is not defined.',
      recorderMimeTypes.length > 0
        ? null
        : 'Recording is captured from the AudioWorklet as raw PCM and written as WAV, which is larger but needs no encoder.',
    ),
  );

  const hasGUM = typeof navigator.mediaDevices?.getUserMedia === 'function';
  rows.push(
    row(
      'get-user-media',
      'recording',
      'Microphone (getUserMedia)',
      hasGUM ? 'full' : 'none',
      hasGUM
        ? 'The API is present. PadLab does not call it until you tap Record, so no permission has been requested to build this report.'
        : window.isSecureContext
          ? 'navigator.mediaDevices.getUserMedia is not implemented.'
          : 'Unavailable because this page is not a secure context. Serve PadLab over HTTPS.',
      hasGUM
        ? null
        : 'Voice Lab is disabled and says why. Sounds can still be imported from files and built in the synthesiser.',
    ),
  );

  // --- Storage -------------------------------------------------------------------------

  rows.push(
    row(
      'opfs',
      'storage',
      'Origin-private file system',
      opfs.available && opfs.writable ? 'full' : opfs.available ? 'partial' : 'none',
      opfs.reason,
      opfs.available && opfs.writable
        ? null
        : 'Audio is stored as Blobs inside IndexedDB. Functionally identical for PadLab; slower for very large takes because there is no streaming write.',
    ),
  );

  rows.push(
    row(
      'opfs-sync-access',
      'storage',
      'OPFS synchronous access handles',
      opfs.syncAccess ? 'full' : 'none',
      opfs.syncAccess
        ? 'createSyncAccessHandle() is available, so a worker can stream a recording straight to disk.'
        : 'createSyncAccessHandle() is not available on file handles in this context.',
      opfs.syncAccess
        ? null
        : 'A long recording is buffered in memory and flushed asynchronously in chunks, so a very long take costs RAM while it runs.',
    ),
  );

  rows.push(
    row(
      'indexeddb',
      'storage',
      'IndexedDB',
      idb.ok ? 'full' : 'none',
      idb.reason,
      idb.ok
        ? null
        : 'PadLab cannot save anything on this device. It runs in a session-only mode and says so before you record.',
    ),
  );

  rows.push(
    row(
      'persistent-storage',
      'storage',
      'Persistent storage',
      storage.persisted ? 'full' : storage.canRequestPersistence ? 'partial' : 'none',
      storage.persisted
        ? 'Granted. The browser will not evict PadLab’s projects or recordings under storage pressure.'
        : storage.canRequestPersistence
          ? 'The API exists but persistence has not been granted. Safari grants it only after the app is added to the Home Screen and used repeatedly.'
          : 'navigator.storage.persist is not implemented.',
      storage.persisted
        ? null
        : 'Projects can be evicted if the device runs out of space. PadLab keeps an exportable backup path open and warns before storage gets tight.',
    ),
  );

  // --- System --------------------------------------------------------------------------

  const hasSW = 'serviceWorker' in navigator;
  rows.push(
    row(
      'service-worker',
      'system',
      'Service worker',
      hasSW ? 'full' : 'none',
      hasSW
        ? 'Registered as a classic script, so no module-worker support is required.'
        : window.isSecureContext
          ? 'navigator.serviceWorker is not available.'
          : 'Unavailable because this page is not a secure context.',
      hasSW ? null : 'PadLab runs online only. Nothing is cached and the offline screen never appears.',
    ),
  );

  const hasSync =
    typeof ServiceWorkerRegistration === 'function' && 'sync' in ServiceWorkerRegistration.prototype;
  rows.push(
    row(
      'background-sync',
      'system',
      'Background Sync',
      hasSync ? 'full' : 'none',
      hasSync
        ? 'SyncManager is available.'
        : 'SyncManager is not implemented. WebKit has never shipped Background Sync.',
      hasSync
        ? null
        : 'Queued marketplace work is retried the next time you open PadLab with a connection. Nothing is lost; it just is not sent while the app is closed.',
    ),
  );

  const hasPush = 'PushManager' in window && 'Notification' in window;
  rows.push(
    row(
      'push',
      'system',
      'Push notifications',
      hasPush ? 'full' : 'none',
      hasPush
        ? 'PushManager and Notification are available. On iOS this requires the app to be on the Home Screen.'
        : 'PushManager is not available in this browsing mode.',
      hasPush
        ? null
        : 'PadLab has no push-driven feature; the marketplace shows pack updates when you open it.',
    ),
  );

  const hasBadging = typeof (navigator as Navigator & { setAppBadge?: unknown }).setAppBadge === 'function';
  rows.push(
    row(
      'badging',
      'system',
      'App icon badging',
      hasBadging ? 'full' : 'none',
      hasBadging ? 'navigator.setAppBadge is available.' : 'navigator.setAppBadge is not implemented.',
      hasBadging ? null : 'Counts appear inside the app instead of on the Home Screen icon.',
    ),
  );

  const hasShare = typeof navigator.share === 'function';
  const hasShareFiles = probeShareFiles();
  rows.push(
    row(
      'web-share',
      'system',
      'Web Share',
      hasShare ? 'full' : 'none',
      hasShare ? 'navigator.share opens the system share sheet.' : 'navigator.share is not implemented.',
      hasShare ? null : 'Sharing falls back to a download with the correct filename plus a copy-link action.',
    ),
  );
  rows.push(
    row(
      'web-share-files',
      'system',
      'Web Share with files',
      hasShareFiles ? 'full' : 'none',
      hasShareFiles
        ? 'canShare({ files }) accepted a WAV file, so exports go straight to the share sheet.'
        : 'canShare({ files }) refused an audio file, so only text and URLs can be shared.',
      hasShareFiles ? null : 'Audio exports are downloaded to Files instead of handed to the share sheet.',
    ),
  );

  const hasFSA =
    typeof (window as Window & { showSaveFilePicker?: unknown }).showSaveFilePicker === 'function';
  rows.push(
    row(
      'file-system-access',
      'system',
      'File System Access',
      hasFSA ? 'full' : 'none',
      hasFSA
        ? 'showOpenFilePicker/showSaveFilePicker are available, so exports can be written where you choose.'
        : 'showSaveFilePicker is not implemented. WebKit exposes only the origin-private file system.',
      hasFSA
        ? null
        : 'Import uses a file input; export uses a download. Both reach the Files app on iOS, they just cannot write in place.',
    ),
  );

  const hasWakeLock = 'wakeLock' in navigator;
  rows.push(
    row(
      'wake-lock',
      'system',
      'Screen Wake Lock',
      hasWakeLock ? 'full' : 'none',
      hasWakeLock
        ? 'navigator.wakeLock is available and is taken while the transport is running.'
        : 'navigator.wakeLock is not implemented in this browser.',
      hasWakeLock
        ? null
        : 'The screen dims on the system timer during a performance. PadLab keeps playing; only the display sleeps.',
    ),
  );

  const hasMediaSession = 'mediaSession' in navigator;
  rows.push(
    row(
      'media-session',
      'system',
      'Media Session',
      hasMediaSession ? 'partial' : 'none',
      hasMediaSession
        ? 'Metadata and play/pause/stop handlers are registered. iOS only surfaces Lock Screen controls for media-element playback, so a pure Web Audio graph may not appear there.'
        : 'navigator.mediaSession is not implemented.',
      'Transport control always remains available inside the app, and headset play/pause is handled where the browser delivers it.',
    ),
  );

  // Whether PadLab can declare itself an instrument rather than a video player. Worth
  // reporting because it is the difference between pads that play through the Ring/Silent
  // switch and pads that go silent for a reason nothing on screen explains.
  const sessionType = (navigator as Navigator & { audioSession?: { type?: string } }).audioSession?.type;
  rows.push(
    row(
      'audio-session',
      'audio',
      'Audio session category',
      sessionType === undefined ? 'none' : 'full',
      sessionType === undefined
        ? 'navigator.audioSession is not available, so the Ring/Silent switch decides whether PadLab makes a sound.'
        : `Declared as "${sessionType}", so pads play through the Ring/Silent switch.`,
      'Turn the Ring/Silent switch to Ring if the pads are silent.',
    ),
  );

  const hasVibration = typeof navigator.vibrate === 'function';
  rows.push(
    row(
      'vibration',
      'system',
      'Haptics (Vibration API)',
      hasVibration ? 'partial' : 'none',
      hasVibration
        ? 'navigator.vibrate exists. It is a blunt buzz, not the native Taptic Engine patterns.'
        : 'navigator.vibrate is not implemented. iOS has never exposed it to the web.',
      'Pad feedback is carried by the visual press state and the sound itself, which are the signals the design does not let haptics replace.',
    ),
  );

  // --- Display -------------------------------------------------------------------------

  const hasViewTransitions =
    typeof (document as Document & { startViewTransition?: unknown }).startViewTransition ===
    'function';
  rows.push(
    row(
      'view-transitions',
      'display',
      'View Transitions',
      hasViewTransitions ? 'full' : 'none',
      hasViewTransitions
        ? 'document.startViewTransition is available for shared-element transitions.'
        : 'document.startViewTransition is not implemented.',
      hasViewTransitions ? null : 'The equivalent CSS transitions run instead; the motion is the same, the element morph is not.',
    ),
  );

  const installed = display.displayMode !== 'browser' || display.legacyStandalone;
  rows.push(
    row(
      'standalone-display',
      'display',
      'Standalone display mode',
      installed ? 'full' : 'partial',
      installed
        ? `Running as an installed app (display-mode: ${display.displayMode}${display.legacyStandalone ? ', navigator.standalone' : ''}).`
        : 'Running inside browser chrome. Add PadLab to the Home Screen for the full-bleed layout and persistent storage.',
      installed ? null : 'The in-browser layout reserves room for the browser UI and shows the install guide.',
    ),
  );

  const hasInstallPrompt = 'onbeforeinstallprompt' in window;
  rows.push(
    row(
      'install-prompt',
      'display',
      'Install prompt (beforeinstallprompt)',
      hasInstallPrompt ? 'full' : 'none',
      hasInstallPrompt
        ? 'The browser offers a programmatic install prompt.'
        : 'beforeinstallprompt does not exist here. Safari has no programmatic install.',
      hasInstallPrompt ? null : 'PadLab shows its own Add to Home Screen guide with the Share-sheet steps.',
    ),
  );

  return {
    generatedAt: new Date().toISOString(),
    secureContext: window.isSecureContext,
    crossOriginIsolated: isolated,
    capabilities: rows,
    recorderMimeTypes,
    audio,
    storage,
    display,
  };
}

// MARK: - Presentation

const SUPPORT_LABEL: Record<SupportLevel, string> = {
  full: 'Supported',
  partial: 'Limited',
  none: 'Unsupported',
};

/** A glyph, so support level is never carried by colour alone. */
const SUPPORT_GLYPH: Record<SupportLevel, string> = {
  full: '●',
  partial: '◐',
  none: '○',
};

const SUPPORT_COLOR: Record<SupportLevel, string> = {
  full: 'var(--accent-mint)',
  partial: 'var(--accent-amber)',
  none: 'var(--tone-tertiary)',
};

const GROUP_LABEL: Record<CapabilityGroup, string> = {
  audio: 'Audio engine',
  recording: 'Recording',
  storage: 'Storage',
  system: 'System integration',
  display: 'Display and install',
};

const GROUP_ORDER: CapabilityGroup[] = ['audio', 'recording', 'storage', 'system', 'display'];

function formatBytes(value: number | null): string {
  if (value === null) return 'Not reported';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function formatMilliseconds(seconds: number | null): string {
  return seconds === null ? 'Not reported' : `${(seconds * 1000).toFixed(2)} ms`;
}

function environmentPairs(report: CapabilityReport): Array<[string, string]> {
  const { audio, storage, display } = report;
  return [
    [
      'Sample rate',
      audio.sampleRate === null ? 'Not reported' : `${(audio.sampleRate / 1000).toFixed(3)} kHz`,
    ],
    [
      'Base latency',
      `${formatMilliseconds(audio.baseLatencySeconds)}${audio.source === 'probe' ? ' (probe context)' : ''}`,
    ],
    ['Output latency', formatMilliseconds(audio.outputLatencySeconds)],
    ['Output channels', audio.maxChannelCount === null ? 'Not reported' : String(audio.maxChannelCount)],
    ['Recording formats', report.recorderMimeTypes.join(', ') || 'none accepted'],
    ['Storage used', formatBytes(storage.usageBytes)],
    ['Storage quota', formatBytes(storage.quotaBytes)],
    ['Storage persisted', storage.persisted ? 'yes' : 'no'],
    ['Secure context', report.secureContext ? 'yes' : 'no'],
    ['Cross-origin isolated', report.crossOriginIsolated ? 'yes' : 'no'],
    ['Display mode', display.displayMode],
    [
      'Viewport',
      `${display.viewportWidth} × ${display.viewportHeight} @ ${display.devicePixelRatio}×`,
    ],
  ];
}

/**
 * The settings screen's capability table.
 *
 * Styling is written through the CSSOM by the DOM helper (never a `style` attribute), so the
 * page keeps its no-`unsafe-inline` policy, and every value is a design token rather than a
 * literal so this table cannot drift away from the rest of the app.
 */
export function renderCapabilityTable(report: CapabilityReport): HTMLElement {
  const cellPadding = 'var(--space-4) var(--space-3)';

  const environment = h(
    'dl',
    {
      class: 'callout',
      style: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        gap: 'var(--space-5) var(--space-6)',
        marginBottom: 'var(--space-7)',
      },
    },
    ...environmentPairs(report).map(([label, value]) =>
      h(
        'div',
        null,
        h('dt', { class: 'kicker' }, label),
        h(
          'dd',
          {
            class: 'tabular',
            style: {
              marginTop: 'var(--space-1)',
              fontSize: 'var(--type-callout)',
              color: 'var(--tone-primary)',
              overflowWrap: 'anywhere',
            },
          },
          value,
        ),
      ),
    ),
  );

  const sections = GROUP_ORDER.map((group) => {
    const rows = report.capabilities.filter((entry) => entry.group === group);
    if (rows.length === 0) return null;

    const body = h(
      'tbody',
      null,
      ...rows.map((entry) =>
        h(
          'tr',
          { style: { borderTop: 'var(--hairline-width) solid var(--hairline)' } },
          h(
            'th',
            {
              scope: 'row',
              style: {
                padding: cellPadding,
                textAlign: 'left',
                fontSize: 'var(--type-callout)',
                fontWeight: 'var(--weight-semibold)',
                color: 'var(--tone-primary)',
                verticalAlign: 'top',
              },
            },
            entry.label,
          ),
          h(
            'td',
            {
              style: {
                padding: cellPadding,
                verticalAlign: 'top',
                whiteSpace: 'nowrap',
                color: SUPPORT_COLOR[entry.support],
                fontSize: 'var(--type-caption)',
                fontWeight: 'var(--weight-semibold)',
              },
            },
            h('span', { 'aria-hidden': 'true', style: { marginRight: 'var(--space-2)' } }, SUPPORT_GLYPH[entry.support]),
            SUPPORT_LABEL[entry.support],
          ),
          h(
            'td',
            {
              style: {
                padding: cellPadding,
                verticalAlign: 'top',
                color: 'var(--tone-secondary)',
                fontSize: 'var(--type-caption)',
                lineHeight: 'var(--leading-normal)',
              },
            },
            entry.detail,
            entry.fallback === null
              ? null
              : h(
                  'span',
                  { style: { display: 'block', marginTop: 'var(--space-3)' } },
                  h('span', { class: 'kicker' }, 'Instead'),
                  h(
                    'span',
                    { style: { display: 'block', marginTop: 'var(--space-1)' } },
                    entry.fallback,
                  ),
                ),
          ),
        ),
      ),
    );

    return h(
      'section',
      { style: { marginBottom: 'var(--space-8)' } },
      h('h3', { class: 'kicker', style: { marginBottom: 'var(--space-4)' } }, GROUP_LABEL[group]),
      // The table scrolls inside its own box so a long detail sentence can never make the
      // whole settings sheet scroll sideways.
      h(
        'div',
        { class: 'scroll-x' },
        h(
          'table',
          {
            style: {
              width: '100%',
              minWidth: '30rem',
              borderCollapse: 'collapse',
              textAlign: 'left',
            },
          },
          h(
            'caption',
            { class: 'visually-hidden' },
            `${GROUP_LABEL[group]} capabilities detected on this device`,
          ),
          body,
        ),
      ),
    );
  }).filter((node): node is HTMLElement => node !== null);

  return h(
    'div',
    { class: 'capability-report' },
    h('p', { class: 'kicker' }, 'Measured on this device'),
    h(
      'p',
      {
        style: {
          margin: 'var(--space-3) 0 var(--space-6)',
          color: 'var(--tone-secondary)',
          fontSize: 'var(--type-caption)',
        },
      },
      'Everything below was tested when this screen opened. No permission was requested to produce it.',
    ),
    environment,
    ...sections,
  );
}

/**
 * The same report as Markdown, for pasting into docs/CAPABILITY-MATRIX.md. Kept in this file
 * so the doc and the settings screen can never disagree about what was detected.
 */
export function capabilityMarkdown(report: CapabilityReport): string {
  const lines: string[] = [];

  lines.push('## PadLab PWA: detected capabilities');
  lines.push('');
  lines.push(`Generated ${report.generatedAt} on the device that ran the probe.`);
  lines.push('');

  lines.push('### Environment');
  lines.push('');
  lines.push('| Measurement | Value |');
  lines.push('| --- | --- |');
  for (const [label, value] of environmentPairs(report)) {
    lines.push(`| ${label} | ${escapePipes(value)} |`);
  }
  lines.push('');

  for (const group of GROUP_ORDER) {
    const rows = report.capabilities.filter((entry) => entry.group === group);
    if (rows.length === 0) continue;
    lines.push(`### ${GROUP_LABEL[group]}`);
    lines.push('');
    lines.push('| Capability | Support | Observed | Fallback shipped |');
    lines.push('| --- | --- | --- | --- |');
    for (const entry of rows) {
      lines.push(
        `| ${escapePipes(entry.label)} | ${SUPPORT_LABEL[entry.support]} | ${escapePipes(entry.detail)} | ${escapePipes(entry.fallback ?? 'None')} |`,
      );
    }
    lines.push('');
  }

  return lines.join('\n');
}

function escapePipes(value: string): string {
  return value.replace(/\|/g, '\\|');
}
